import { useEffect } from 'react';

/**
 * 保护输入框在编辑时不因鼠标操作（拖拽到外部松手等）而误触发 React Flow 的取消选中/Blur。
 * 使用方法：
 *   const inputRef = useRef(null);
 *   useInputProtection(inputRef, isEditing);
 * 
 * 逻辑：在 Capture 阶段拦截全局的 mouseup/pointerup/click 事件。
 * 如果当前焦点还在 inputRef 内，就阻止冒泡，防止外层（图板）接收到事件。
 */
export function useInputProtection(ref, active = true) {
  useEffect(() => {
    if (!active || !ref.current) return;

    const shouldBlock = (e) => {
      // 检查当前焦点元素是否是受保护的元素（或其子元素）
      if (document.activeElement && (document.activeElement === ref.current || ref.current.contains(document.activeElement))) {
        
        // 【关键修复】如果事件的目标元素本身就在受保护区域内（例如点击了内部的按钮、输入框），则说明是正常的内部交互，
        // 不需要拦截。我们只拦截那些“焦点在内部，但点击/操作在外部”的情况（除了特定需求）。
        // 注意：e.target 是实际触发事件的元素
        if (ref.current && (e.target === ref.current || ref.current.contains(e.target))) {
            return;
        }
        // 【新增】如果点击的是 Modal 弹窗内的元素（z-index >= 4000），不要拦截
        // 这样可以让 ApiConfigModal 等弹窗内的下拉框正常工作
        const targetElement = e.target;
        if (targetElement) {
          let el = targetElement;
          while (el && el !== document.body) {
            const zIndex = window.getComputedStyle(el).zIndex;
            if (zIndex && parseInt(zIndex) >= 4000) {
              // 这是一个高层级弹窗内的元素，不要拦截
              return;
            }
            el = el.parentElement;
          }
        }
        e.stopPropagation();
        // e.preventDefault(); // 注意：preventDefault 可能会导致 input 无法 selection，慎用，这里主要阻止冒泡
        // 但对于 mouseup，如果不 preventDefault，可能还会触发 click
        if (e.type !== 'mousedown' && e.type !== 'pointerdown') {
            // 对于 up / click 事件，如果发生在外部，我们希望 Input 保持焦点，且不触发外层的 click
             // stopImmediatePropagation 是最强力的
             if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
        }
      }
    };

    // 事件列表：mousedown 防止点击打断，mouseup 防止拖拽松手，click 防止点击判定
    const events = ['mousedown', 'mouseup', 'click', 'pointerdown', 'pointerup'];

    events.forEach(evt => {
        document.addEventListener(evt, shouldBlock, true); // Use capture
    });

    return () => {
        events.forEach(evt => {
            document.removeEventListener(evt, shouldBlock, true);
        });
    };
  }, [active, ref]);
}
